import { Business, TenantBusiness, DiscountCategory } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Select, NumberInput } from '@mantine/core';
import { useState, ReactElement } from 'react';
import CardModal from '../card-modal';
import CurrencySelector from '../currency-selector';
import { useUpdateTenantBusiness, useDiscountCategories } from '@/hooks/api-hooks';
import { IconExternalLink } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

interface Props {
    businesses: Business[];
    target: ReactElement;
    onSuccess?: () => void;
}

interface BusinessUpdateStatus {
    id: number;
    loading: boolean;
    success: boolean | null;
}

const DELAY = 350; // ms

export default function BulkBusinessEditModal({ businesses, target, onSuccess }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    // Form fields states
    const [defaultCurrency, setDefaultCurrency] = useState<string | null>('');
    const [discountCategory, setDiscountCategory] = useState<string>('');
    const [rank, setRank] = useState<number | '' | null>('');

    // Update progress states
    const [updateStatuses, setUpdateStatuses] = useState<Record<number, BusinessUpdateStatus>>({});
    const [counter, setCounter] = useState<number>(0);

    const updateTenantBusinessMutation = useUpdateTenantBusiness();
    const { data: discountCategories } = useDiscountCategories();
    const discountCategoriesData = discountCategories?.data || [];

    const hasStarted = Object.keys(updateStatuses).length > 0;
    const allCompleted = hasStarted && Object.values(updateStatuses).every(status => !status.loading);

    const getUpdates = (tb: TenantBusiness): Partial<TenantBusiness> => {
        const updates: Partial<TenantBusiness> = {};
        if (defaultCurrency !== '') {
            updates.default_currency = defaultCurrency;
        }
        if (discountCategory !== '') {
            updates.discount_category = discountCategory;
        }
        if (rank !== '') {
            updates.rank = rank;
        }
        return updates;
    };

    const handleBulkUpdate = async () => {
        const validBusinesses = businesses.filter(b => b.tenant_business);
        if (validBusinesses.length === 0) {
            setOpened(false);
            if (onSuccess) onSuccess();
            return;
        }

        const initialStatuses: Record<number, BusinessUpdateStatus> = {};
        validBusinesses.forEach(b => {
            initialStatuses[b.id] = { id: b.id, loading: true, success: null };
        });
        setUpdateStatuses(initialStatuses);
        setCounter(0);

        let completed = 0;
        for (let i = 0; i < validBusinesses.length; i++) {
            const business = validBusinesses[i];
            const tb = business.tenant_business!;

            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, DELAY));
            }

            try {
                const updates = getUpdates(tb);
                // Only call save if we have actual modifications
                if (Object.keys(updates).length > 0) {
                    await updateTenantBusinessMutation.mutateAsync({
                        id: tb.id,
                        ...updates,
                    });
                }
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [business.id]: { id: business.id, loading: false, success: true }
                }));
            } catch (error) {
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [business.id]: { id: business.id, loading: false, success: false }
                }));
            }
        }

        if (onSuccess) onSuccess();
    };

    const handleClose = () => {
        setOpened(false);
        // Reset states
        setDefaultCurrency('');
        setDiscountCategory('');
        setRank('');
        setUpdateStatuses({});
        setCounter(0);
    };

    return (
        <>
            <div onClick={() => setOpened(true)} className='cursor-pointer'>
                {target}
            </div>

            <CardModal
                opened={opened}
                onClose={handleClose}
                header={`${t('bulk_edit_businesses')} (${businesses.length})`}
                size="lg"
            >
                <div className="space-y-6">
                    {/* Inputs Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                        <CurrencySelector
                            label={t('default_currency')}
                            placeholder={t('no_change') || 'No change'}
                            value={defaultCurrency}
                            onChange={setDefaultCurrency}
                            clearable
                            className="w-full font-semibold"
                        />
                        <Select
                            label={t('discount_category')}
                            placeholder={t('no_change') || 'No change'}
                            value={discountCategory || null}
                            data={discountCategoriesData.map((v: DiscountCategory) => v.category)}
                            onChange={(value) => setDiscountCategory(value || '')}
                            clearable
                            className="font-semibold w-full"
                        />
                        <NumberInput
                            label={t('public_rank')}
                            placeholder={t('no_change') || 'No change'}
                            value={rank === '' ? undefined : (rank as any)}
                            onChange={(value) => setRank(value === '' || value === null || value === undefined ? null : Number(value))}
                            min={0}
                            hideControls
                            className="font-semibold w-full"
                        />
                    </div>

                    {/* Selected Businesses List */}
                    <div className="space-y-2 solid-box-border text-xs max-h-48 overflow-y-auto">
                        <div className="font-semibold text-gray-500 mb-1">{t('selected') || 'Selected'}:</div>
                        {businesses.map(b => (
                            <div key={b.id} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-b-0 gap-2">
                                <Link
                                    to={`/businesses/${b.id}`}
                                    target="_blank"
                                    className="flex items-center gap-1 hover:underline text-blue-600 font-medium min-w-0 flex-1 mr-2"
                                >
                                    <IconExternalLink size={14} className="shrink-0" />
                                    <span className="truncate">{b.name} (ID: #{b.id})</span>
                                </Link>
                                {updateStatuses[b.id] && (
                                    <div className="text-xxs shrink-0">
                                        {updateStatuses[b.id].loading && <Loader size="xs" color="blue" />}
                                        {updateStatuses[b.id].success === true && <span className="text-green-600 font-bold">✓</span>}
                                        {updateStatuses[b.id].success === false && <span className="text-red-600 font-bold">✗</span>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Footer Actions / Progress */}
                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        {!hasStarted ? (
                            <div className="flex gap-2">
                                <Button variant="default" onClick={handleClose} radius="xl" size="xs">
                                    {t('cancel')}
                                </Button>
                                <Button
                                    onClick={handleBulkUpdate}
                                    color="black"
                                    radius="xl"
                                    size="xs"
                                    disabled={defaultCurrency === '' && discountCategory === '' && rank === ''}
                                >
                                    {t('apply_changes')}
                                </Button>
                            </div>
                        ) : allCompleted ? (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-green-600">{t('done')}!</span>
                                <Button onClick={handleClose} color="black" radius="xl" size="xs">
                                    {t('close')}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                    {t('updating')} {counter} / {businesses.length}
                                </span>
                                <Loader size="xs" color="black" />
                            </div>
                        )}
                    </div>
                </div>
            </CardModal>
        </>
    );
}
